import { CommonModule } from '@angular/common';
import { Component, OnInit, TemplateRef, inject } from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { Subscription, map, switchMap, take, EMPTY, forkJoin } from 'rxjs';
import { CustomersService } from '../../../services/customers/customers.service';
import { Customer } from '../../../model/models';
import { ImporterAccountComponent } from '../../importer-accounts/importer-accounts.component';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { CustomerFeaturesComponent } from '../customer-features/customer-features.component';
import { NzNotificationModule, NzNotificationService } from 'ng-zorro-antd/notification';
import { Buffer } from 'buffer';
import { Router } from '@angular/router';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { FilterComponent } from '../../filter/filter.component';
import { CustomerUsersComponent } from "../customer-users/customer-users.component";
import { CustomerDataAttributesComponent } from "./customer-data-attributes/customer-data-attributes.component";
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzCardModule } from 'ng-zorro-antd/card';
import { CustomerKeyaccountsComponent } from "../customer-keyaccounts/customer-keyaccounts.component";
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CustomerLocationsComponent } from "../customer-locations/customer-locations.component";
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { ApiKeysComponent } from '../api-keys/api-keys.component';
import { CustomerDashboardComponent } from '../customer-dashboard/customer-dashboard.component';

export interface ICustomerMatchingRule {
  matching_rule: string
}

export interface ICustomerArchivingRule {
  archiving_rule: string
}
@Component({
  selector: 'customer',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzFormModule, NzIconModule, NzInputModule, NzSelectModule, NzTabsModule,
    ImporterAccountComponent, NzSkeletonModule, NzDividerModule, CustomerFeaturesComponent, NzNotificationModule, FilterComponent,
    CustomerUsersComponent, CustomerDataAttributesComponent, NzSkeletonModule, NzPopoverModule, NzCardModule, CustomerKeyaccountsComponent, NzSpinModule, CustomerLocationsComponent, NzCheckboxModule, ApiKeysComponent, CustomerDashboardComponent],
  templateUrl: './customer.component.html',
  styleUrl: './customer.component.scss'
})
export class CustomerComponent implements OnInit {
  private subscription = new Subscription();
  route = inject(ActivatedRoute);
  customersService = inject(CustomersService);
  notification = inject(NzNotificationService);
  // customer: Customer = {} as Customer;
  customer: any = {
    company_name: '',
    billing_address_phone: '',
    billing_address_street: '',
    billing_address_street_no: '',
    billing_address_zip_code: '',
    billing_address_city: '',
    billing_address_country: '',
    admin_email: '',
    exclude_statistics: true
  };
  customersFeatures: any[] = new Array();
  infoLoading = true;
  saving = false;
  isCreatingCustomer = false;
  formChanged = false;
  originalCustomer: Customer | null = null;
  router = inject(Router);
  modal = inject(NzModalService);
  matchingRules = this.customersService.getMatchingRules()
  archivingRules = this.customersService.getArchivingRules()
  customerMatchingRule?: ICustomerMatchingRule
  customerArchivingRule?: ICustomerArchivingRule
  matchingRuleLoading = true
  archivingRuleLoading = true
  archivingMap = new Map(Object.entries({
    'cardealer': 'MA-Rule',
    'leasing': 'PLC-Rule',
    'fleet': 'PFM-Rule',
  }))
  matchingMap = new Map(Object.entries({
    'cardealer': 'MA-Data',
    'leasing': 'PLC-Data [with MA-Match]',
    'fleet': 'PFM- and PLC-Data [with Ma-Match]',
  }))
  keyAccountsList: any
  usersList: any
  keyAccountsLoading = true;
  usersLoading = true;

  ngOnInit(): void {
    this.subscription.add(
      this.route.params.pipe(
        map((params: Params) => {
          return params['id'];
        }),
        switchMap(id => {
          if (id === 'create') {
            this.infoLoading = false;
            this.isCreatingCustomer = true;
            this.customerMatchingRule = { matching_rule: '' };
            this.matchingRuleLoading = false;
            return EMPTY;
          }
          return this.customersService.getCustomersById(id);
        }),
        switchMap((res: any) => {
          this.customer = res;
          this.originalCustomer = JSON.parse(JSON.stringify(res));
          this.formChanged = false;
          this.infoLoading = false;
          return forkJoin({
            matchingRule: this.customersService.getCustomerMatchingRule(this.customer.pk).pipe(take(1)),
            archivingRule: this.customersService.getCustomerArchivingRule(this.customer.pk).pipe(take(1)),
            keyAccounts: this.customersService.getKeyAccounts(this.customer.pk).pipe(take(1)),
            users: this.customersService.getUsers(this.customer.pk).pipe(take(1)),
          })
        })
      ).subscribe(({ matchingRule, archivingRule, keyAccounts, users }) => {
        this.customerMatchingRule = matchingRule
        this.matchingRuleLoading = false
        this.customerArchivingRule = archivingRule
        this.archivingRuleLoading = false
        this.keyAccountsList = keyAccounts
        this.usersList = users
        this.keyAccountsLoading = false;
        this.usersLoading = false
      })
    );
  }

  saveChanges(customerForm: NgForm) {
    this.saving = true;
    if (!this.customer.pk) {
      this.customer = { ...this.customer, ...this.customerMatchingRule };
    } else {
      delete this.customer.matching_rule;
    }

    if (this.isCreatingCustomer && !this.customer.pk) {
      const payload =
        this.customersService.createNewCustomer(this.customer).subscribe({
          next: (res) => {
            this.saving = false;
            this.customer = res;
            this.formChanged = false;
            this.notification.success(
              "",
              `New tenant created successfully`
            );
          },
          error: (err) => {
            this.saving = false;
            this.notification.error(
              "",
              err.message
            );
          }
        });
    } else {
      const customerToUpdate = { ...this.customer };
      delete (customerToUpdate as any).admin_email;
      this.customersService.editCustomer(customerToUpdate).pipe(
        switchMap(res => this.customersService.getCustomersById(Buffer.from(res.pk).toString('base64')))
      ).subscribe({
        next: (res: any) => {
          this.customer = res;
          this.originalCustomer = JSON.parse(JSON.stringify(res));
          this.formChanged = false;
          this.saving = false;
          this.notification.success(
            "",
            `Changes to tenant ${this.customer.company_name} saved`
          );
        },
        error: (err: any) => {
          console.log(err);
          this.notification.error('Error', err.error.message);
          this.saving = false;
        }
      })
    }
  }

  onFormChange() {
    if (this.isCreatingCustomer) {
      this.formChanged = true;
    } else if (this.originalCustomer) {
      this.formChanged = JSON.stringify(this.customer) !== JSON.stringify(this.originalCustomer);
    }
  }

  createFilterModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>) {
    let nzTitle = 'Filter hinzufügen';
    let data = {};
    const modal: NzModalRef = this.modal.create({
      nzTitle: nzTitle,
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzMaskClosable: false,
      nzData: data
    });
  }

  saveMatchingRule(ngform: NgForm) {
    this.matchingRuleLoading = true
    this.customersService.setCustomerMatchingRule(this.customer.pk, this.customerMatchingRule!)
      .subscribe(res => {
        this.matchingRuleLoading = false
        ngform.form.markAsPristine()
      })
  }

  saveArchivingRule(ngform: NgForm) {
    this.archivingRuleLoading = true
    this.customersService.setCustomerArchivingRule(this.customer.pk, this.customerArchivingRule!)
      .subscribe(res => {
        this.archivingRuleLoading = false
        ngform.form.markAsPristine()
      })
  }
}
